import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, provide } from 'vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { OfferLocation } from '@/helpers/offer-helper';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';

import type { BuyboxRegularOffer, UpcomingReleaseBuybox } from './types';

import { SnowplowClickoutContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

import { trackFreeTrialSwiperExp } from '../experiments/FreeTrialSwiperExp/tracking';
import { TitleOfferFragment } from '../buybox/graphql/fragments/Offer.fragment';
import { useBuyboxLoggedInExp } from '../experiments/BuyboxLoggedInExp/composables/useBuyboxLoggedInExp';
import { qualityTVProviderConfig } from '@/components/buybox/QualityTvRow/data';


export default /*#__PURE__*/_defineComponent({
  __name: 'RegularBuyBoxNew',
  props: {
    regularOffers: null,
    allOffers: null,
    filter: null,
    title: null,
    additionalContexts: null,
    upcomingReleases: null,
    isInWatchlist: { type: Boolean },
    seasonNumber: null,
    episodeNumber: null,
    isEpisodeBuybox: { type: Boolean }
  },
  emits: ["onNotifyMe"],
  setup(__props: any) {

const props = __props as {
	regularOffers: BuyboxRegularOffer[];
	allOffers: TitleOfferFragment[];
	filter: BuyBoxFilterOptions;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	upcomingReleases: UpcomingReleaseBuybox[];
	isInWatchlist: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	isEpisodeBuybox?: boolean;
};

const BuyBoxUpcomingNew = () => import('@/components/buybox-new/BuyBoxUpcomingNew.vue');
const BuyBoxOfferNew = () => import('@/components/buybox-new/BuyBoxOfferNew.vue');
const BuyBoxNoOffersNew = () => import('@/components/buybox-new/BuyBoxNoOffersNew.vue');
const BuyBoxNotifyMeNew = () => import('@/components/buybox-new/BuyBoxNotifyMeNew.vue');
const BuyBoxPromotionRowNew = () => import('@/components/buybox-new/BuyBoxPromotionRowNew.vue');





const { language } = useLanguageStore();

const buyboxContainer = ref<HTMLElement | null>(null);
provide('offerLocation', OfferLocation.REGULAR_BUYBOX);

const showFreeTrial = computed(
	() =>
		!!promotionOffer.value &&
		[BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.FREE, BuyBoxFilterOptions.SUBSCRIPTION].includes(props.filter) &&
		// splitting language for locale specific ones like Spanish - Spain, Spanish - Latin America, etc
		allowedPromotionLanguages.includes(language.value.split('-')[0])
);

const hasFreeTrialOffers = computed(() => props?.regularOffers.length > 0 || inPromotionCatalogue.value);

const showNoOffers = computed(() => props.regularOffers.length === 0);

const showNotifyMeNew = computed(() => {
	return props.filter === BuyBoxFilterOptions.FREE ? hasFreeTrialOffers.value : !showNoOffers.value;
});

const hasOnlyUpcomingOffers = computed(() => props.upcomingReleases.length > 0 && showNoOffers.value);

//  Quality TV
const qualityTvTrackingContexts = computed(() => {
	const titleObjectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;
	const titleObjectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const titleContext = new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		props.seasonNumber ?? null,
		props.episodeNumber ?? null
	);

	return (props.additionalContexts || []).concat([titleContext]);
});
// Quality TV

const { promotionOffer, inPromotionCatalogue, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => props.allOffers,
});

// FAUX_SWIPER_EXP
const fauxSwiperExpHasAnimated = ref(false);
function handleFauxSwiperExpAnimation() {
	fauxSwiperExpHasAnimated.value = true;
}
// FAUX_SWIPER_EXP

const includeTVODQTVonRentOrBuy = computed(() => {
	// Include TVOD QTV (which acts as a 'freeTrial' offer) also on Rent and Buy filters. Other free trial offers are not included.
	return (
		catalogueItem.value?.placement === 'qtv' &&
		qualityTVProviderConfig[catalogueItem.value.packageId] &&
		qualityTVProviderConfig[catalogueItem.value.packageId].type === 'TVOD' &&
		[BuyBoxFilterOptions.BUY, BuyBoxFilterOptions.RENT].includes(props.filter)
	);
});

const _allOffers = computed(() => {
	let offers = [...props.regularOffers];

	// Add promotion offer to the list of offers
	if (
		!showNoOffers.value &&
		([BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.SUBSCRIPTION, BuyBoxFilterOptions.FREE].includes(props.filter) ||
			includeTVODQTVonRentOrBuy.value) &&
		!!promotionOffer.value
	) {
		if (offers.length > 2) {
			offers.splice(2, 0, { ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer);
		} else {
			offers.push({ ...promotionOffer.value, rowType: 'freeTrial' } as BuyboxRegularOffer);
		}
	}

	return offers;
});
// BUYBOX_LOGGED_IN_EXP
const { trackBuyboxLoggedInExp } = useBuyboxLoggedInExp();
//BUYBOX_LOGGED_IN_EXP
function clickOut(payload: {
	buyboxOffer: BuyboxRegularOffer;
	isLeavingIconShown: boolean;
	row: HTMLElement | null;
	offerLocation?: OfferLocation;
}) {
	let _offer = payload.buyboxOffer;
	const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(_offer);

	// FT_SWIPER_EXP
	if (!inPromotionCatalogue.value) {
		const prefix =
			payload.offerLocation === OfferLocation.FREE_TRIAL
				? 'free_trial_clickout_' + (_offer.package.packageId === 350 ? 'atp' : 'amp') + '_'
				: 'clickout_';
		trackFreeTrialSwiperExp({
			action: 'click',
			label: props.isEpisodeBuybox ? prefix + 'episode' : prefix + 'buybox',
			contexts: [clickoutContext, ...props.additionalContexts],
		});
	}
	// FT_SWIPER_EXP
	const label =
		payload.offerLocation === OfferLocation.FREE_TRIAL ? 'free_trial_clickout_new_buybox' : 'clickout_new_buybox';
	trackBuyboxLoggedInExp({
		action: 'click',
		label: props.isEpisodeBuybox ? label + '_episode' : label,
		contexts: [clickoutContext],
	});
}

return { __sfc: true,BuyBoxUpcomingNew, BuyBoxOfferNew, BuyBoxNoOffersNew, BuyBoxNotifyMeNew, BuyBoxPromotionRowNew, props, language, buyboxContainer, showFreeTrial, hasFreeTrialOffers, showNoOffers, showNotifyMeNew, hasOnlyUpcomingOffers, qualityTvTrackingContexts, promotionOffer, inPromotionCatalogue, catalogueItem, fauxSwiperExpHasAnimated, handleFauxSwiperExpAnimation, includeTVODQTVonRentOrBuy, _allOffers, trackBuyboxLoggedInExp, clickOut }
}

})